import { defineAsyncComponent } from "vue";
import {
  createRouter,
  createWebHistory,
  RouteLocationNormalized,
} from "vue-router";
import LoadingSpinner from "@two-ui/components/LoadingSpinner.vue";
import AuthLogin from "@two-ui/views/AuthLogin.vue";
import UserSettings from "@two-ui/views/UserSettings.vue";

export function initAccountsRoutes() {
  return [
    {
      path: "/",
      redirect: "/billing",
    },
    {
      path: "/login",
      component: AuthLogin,
      name: "auth-login",
      meta: { preAuth: true },
    },
    {
      path: "/user/settings",
      name: "user-settings",
      component: UserSettings,
    },
    {
      path: "/balances",
      name: "balances",
      redirect: "/billing",
    },
    {
      path: "/billing",
      name: "billing",
      component: async () =>
        defineAsyncComponent({
          loader: () => import("./views/BasePage.vue"),
          loadingComponent: LoadingSpinner,
        }),
      children: subRoutesBilling,
      meta: { requiresCustomerId: true },
    },
    {
      path: "/statements",
      name: "statements",
      component: async () =>
        defineAsyncComponent({
          loader: () => import("./views/BasePage.vue"),
          loadingComponent: LoadingSpinner,
        }),
      children: [
        {
          path: "",
          name: "statements-home",
          component: async () =>
            defineAsyncComponent({
              loader: () => import("./views/Statements.vue"),
              loadingComponent: LoadingSpinner,
            }),
        },
      ],
    },
    {
      path: "/transactions",
      name: "transactions",
      component: async () =>
        defineAsyncComponent({
          loader: () => import("./views/BasePage.vue"),
          loadingComponent: LoadingSpinner,
        }),
      children: [
        {
          path: "",
          name: "transactions-home",
          component: async () =>
            defineAsyncComponent({
              loader: () => import("./views/Transactions.vue"),
              loadingComponent: LoadingSpinner,
            }),
        },
      ],
    },
  ];
}

export const subRoutesBilling = [
  {
    path: "",
    name: "billing-home",
    component: async () =>
      defineAsyncComponent({
        loader: () => import("./views/Home.vue"),
        loadingComponent: LoadingSpinner,
      }),
    meta: {
      breadCrumbTitle: "Balances",
    },
  },
  {
    path: "add-funds",
    name: "add-funds",
    component: async () =>
      defineAsyncComponent({
        loader: () => import("./views/AddFunds.vue"),
        loadingComponent: LoadingSpinner,
      }),
    meta: {
      breadCrumbTitle: "Add funds",
      backHeadingButton: {
        label: "Add funds",
        backRouteName: "billing-home",
        testId: "add-funds-back",
      },
    },
    children: [
      {
        path: "bank-transfer",
        name: "bank-transfer",
        component: async () =>
          defineAsyncComponent({
            loader: () => import("./views/BankTransfer.vue"),
            loadingComponent: LoadingSpinner,
          }),
        meta: {
          breadCrumbTitle: "Bank transfer",
          backHeadingButton: {
            label: "Bank transfer",
            backRouteName: "add-funds",
            testId: "bank-transfer-back",
          },
          keepParams: true,
        },
        children: [
          {
            path: "bank-details",
            name: "bank-details",
            component: async () =>
              defineAsyncComponent({
                loader: () => import("./views/BankDetails.vue"),
                loadingComponent: LoadingSpinner,
              }),
            meta: {
              breadCrumbTitle: "Bank details",
              backHeadingButton: {
                label: "Bank details",
                backRouteName: "bank-transfer",
                testId: "bank-transfer-back",
              },
            },
          },
        ],
      },
      {
        path: "card-top-up",
        name: "card-top-up",
        component: async () =>
          defineAsyncComponent({
            loader: () => import("./views/CardTopUp.vue"),
            loadingComponent: LoadingSpinner,
          }),
        meta: {
          breadCrumbTitle: "Card payment",
        },
        children: [
          {
            path: "card-success/:topup_id",
            name: "card-success",
            meta: {
              backHeadingButton: {
                label: "",
                backRouteName: "add-funds",
                enabled: false,
              },
            },
            props: true,
            component: async () =>
              defineAsyncComponent({
                loader: () => import("./views/TransferTopUpDetails.vue"),
                loadingComponent: LoadingSpinner,
              }),
          },
        ],
      },
      {
        path: "currency-exchange",
        name: "currency-exchange",
        component: async () =>
          defineAsyncComponent({
            loader: () => import("./views/CurrencyExchange.vue"),
            loadingComponent: LoadingSpinner,
          }),
        meta: {
          breadCrumbTitle: "Runa FX",
          backHeadingButton: {
            label: "Runa FX",
            backRouteName: "add-funds",
            testId: "currency-exchange-back-button",
          },
        },
        children: [
          {
            path: "trade-success/:trade_id",
            name: "trade-success",
            meta: {
              backHeadingButton: {
                label: "",
                backRouteName: "add-funds",
                enabled: false,
              },
            },
            component: async () =>
              defineAsyncComponent({
                loader: () => import("./views/CurrencyExchangeReceipt.vue"),
                loadingComponent: LoadingSpinner,
              }),
          },
        ],
      },
    ],
  },
  {
    path: "top-up/:topup_id",
    name: "top-up",
    component: async () =>
      defineAsyncComponent({
        loader: () => import("./views/TransferTopUpDetails.vue"),
        loadingComponent: LoadingSpinner,
      }),
    meta: {
      link: "/billing/top-up",
    },
  },
  {
    path: "trade/:trade_id",
    name: "trade",
    component: async () =>
      defineAsyncComponent({
        loader: () => import("./views/CurrencyExchangeReceipt.vue"),
        loadingComponent: LoadingSpinner,
      }),
    meta: {
      link: "/billing/trade",
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to, from, savedPosition) {
    if (from.meta.scrollTopOnLeave || to.meta.scrollTopOnEnter)
      return {
        top: 0,
        left: 0,
      };
    else if (savedPosition) return savedPosition;
  },
  routes: initAccountsRoutes(),
});

router.beforeEach(
  (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
    // pass query params to route
    if (to.meta.keepParams) {
      const hasQueryStringsFrom = !!Object.keys(from.query).length;
      const hasQueryStringsTo = !!Object.keys(to.query).length;

      if (!hasQueryStringsTo && hasQueryStringsFrom) {
        return { name: to.name!, query: from.query };
      } else {
        return true;
      }
    } else {
      return true;
    }
  }
);

export default router;
